/**
 * The Vietnamese lunisolar calendar, computed for UTC+7 (Hồ Ngọc Đức's algorithm, after Meeus).
 * Vietnam's calendar follows its own time zone, so some years differ from China's (UTC+8) or
 * Korea's (UTC+9): Tết 1985 fell on 21 January in Vietnam but 20 February in China, and Tết 2027
 * falls a day before Seollal. Used only to mark mùng Một and ngày Rằm; nothing is sent anywhere.
 */
export type LunarDate = { day: number; month: number; year: number; leap: boolean };
export const VIETNAM_TIME_ZONE = 7;

const INT = Math.floor;
const PI = Math.PI;

export function julianDay(day: number, month: number, year: number): number {
  const a = INT((14 - month) / 12), y = year + 4800 - a, m = month + 12 * a - 3;
  let jd = day + INT((153 * m + 2) / 5) + 365 * y + INT(y / 4) - INT(y / 100) + INT(y / 400) - 32045;
  if (jd < 2299161) jd = day + INT((153 * m + 2) / 5) + 365 * y + INT(y / 4) - 32083;
  return jd;
}

/** Julian day (with fraction) of the k-th new moon after 1900-01-01. */
function newMoon(k: number): number {
  const T = k / 1236.85, T2 = T * T, T3 = T2 * T, dr = PI / 180;
  let jd = 2415020.75933 + 29.53058868 * k + 0.0001178 * T2 - 0.000000155 * T3;
  jd += 0.00033 * Math.sin((166.56 + 132.87 * T - 0.009173 * T2) * dr);
  const M = 359.2242 + 29.10535608 * k - 0.0000333 * T2 - 0.00000347 * T3;
  const Mpr = 306.0253 + 385.81691806 * k + 0.0107306 * T2 + 0.00001236 * T3;
  const F = 21.2964 + 390.67050646 * k - 0.0016528 * T2 - 0.00000239 * T3;
  // Periodic terms, each with its own sign (Meeus, chapter 49, abridged).
  const C1 = (0.1734 - 0.000393 * T) * Math.sin(M * dr) + 0.0021 * Math.sin(2 * dr * M)
    - 0.4068 * Math.sin(Mpr * dr) + 0.0161 * Math.sin(dr * 2 * Mpr)
    - 0.0004 * Math.sin(dr * 3 * Mpr)
    + 0.0104 * Math.sin(dr * 2 * F) - 0.0051 * Math.sin(dr * (M + Mpr))
    - 0.0074 * Math.sin(dr * (M - Mpr)) + 0.0004 * Math.sin(dr * (2 * F + M))
    - 0.0004 * Math.sin(dr * (2 * F - M)) - 0.0006 * Math.sin(dr * (2 * F + Mpr))
    + 0.0010 * Math.sin(dr * (2 * F - Mpr)) + 0.0005 * Math.sin(dr * (2 * Mpr + M));
  const deltaT = T < -11
    ? 0.001 + 0.000839 * T + 0.0002261 * T2 - 0.00000845 * T3 - 0.000000081 * T * T3
    : -0.000278 + 0.000265 * T + 0.000262 * T2;
  return jd + C1 - deltaT;
}
const newMoonDay = (k: number, tz: number) => INT(newMoon(k) + 0.5 + tz / 24);

/** Sun's ecliptic longitude in radians at a Julian day. */
function sunLongitude(jdn: number): number {
  const T = (jdn - 2451545.0) / 36525, T2 = T * T, dr = PI / 180;
  const M = 357.52910 + 35999.05030 * T - 0.0001559 * T2 - 0.00000048 * T * T2;
  const L0 = 280.46645 + 36000.76983 * T + 0.0003032 * T2;
  let DL = (1.914600 - 0.004817 * T - 0.000014 * T2) * Math.sin(dr * M);
  DL += (0.019993 - 0.000101 * T) * Math.sin(dr * 2 * M) + 0.000290 * Math.sin(dr * 3 * M);
  let L = (L0 + DL) * dr;
  L -= PI * 2 * INT(L / (PI * 2));
  return L;
}
/** Which of the twelve 30° sectors the sun is in at local midnight starting that day. */
const sunSector = (dayNumber: number, tz: number) => INT(sunLongitude(dayNumber - 0.5 - tz / 24) / PI * 6);

function lunarMonth11(year: number, tz: number): number {
  const off = julianDay(31, 12, year) - 2415021;
  const k = INT(off / 29.530588853);
  const nm = newMoonDay(k, tz);
  return sunSector(nm, tz) >= 9 ? newMoonDay(k - 1, tz) : nm;
}

function leapMonthOffset(a11: number, tz: number): number {
  const k = INT((a11 - 2415021.076998695) / 29.530588853 + 0.5);
  let last = 0, i = 1, arc = sunSector(newMoonDay(k + i, tz), tz);
  do { last = arc; i++; arc = sunSector(newMoonDay(k + i, tz), tz); } while (arc !== last && i < 14);
  return i - 1;
}

/** The lunar date for a calendar date (as printed on a Vietnamese wall calendar). */
export function solarToLunar(day: number, month: number, year: number, tz = VIETNAM_TIME_ZONE): LunarDate {
  const dayNumber = julianDay(day, month, year);
  const k = INT((dayNumber - 2415021.076998695) / 29.530588853);
  let monthStart = newMoonDay(k + 1, tz);
  if (monthStart > dayNumber) monthStart = newMoonDay(k, tz);
  let a11 = lunarMonth11(year, tz), b11 = a11, lunarYear: number;
  if (a11 >= monthStart) { lunarYear = year; a11 = lunarMonth11(year - 1, tz); }
  else { lunarYear = year + 1; b11 = lunarMonth11(year + 1, tz); }
  const lunarDay = dayNumber - monthStart + 1;
  const diff = INT((monthStart - a11) / 29);
  let leap = false, lunarMonth = diff + 11;
  if (b11 - a11 > 365) {
    const leapDiff = leapMonthOffset(a11, tz);
    if (diff >= leapDiff) { lunarMonth = diff + 10; if (diff === leapDiff) leap = true; }
  }
  if (lunarMonth > 12) lunarMonth -= 12;
  if (lunarMonth >= 11 && diff < 4) lunarYear -= 1;
  return { day: lunarDay, month: lunarMonth, year: lunarYear, leap };
}

/** The visitor's own calendar day, read as Vietnamese lunar date: mùng Một is the same day for family abroad. */
export function lunarToday(now: Date = new Date()): LunarDate {
  return solarToLunar(now.getDate(), now.getMonth() + 1, now.getFullYear());
}

export type MoonDay = 'mot' | 'ram' | null;
export function moonDay(date: LunarDate): MoonDay {
  return date.day === 1 ? 'mot' : date.day === 15 ? 'ram' : null;
}
